import React, { useState } from 'react';
import { CheckCircle, XCircle, Award, ArrowLeft, Lightbulb } from 'lucide-react';

/**
 * Quiz Component
 * 
 * Interactive quiz with multiple-choice questions
 * Provides immediate feedback and explanations
 */
const QuizComponent = ({ topic, topicTitle, onComplete, onBack }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);

  // Quiz questions for each topic
  const quizzes = {
    introduction: {
      questions: [
        {
          question: 'What is the primary purpose of Agent-to-Agent (A2A) communication?',
          options: [
            'To replace human workers entirely',
            'To enable autonomous AI agents to collaborate without human intervention',
            'To create complex user interfaces',
            'To store data in databases'
          ],
          correct: 1,
          explanation: 'A2A communication enables autonomous AI agents to collaborate seamlessly without requiring constant human coordination, making complex workflows more efficient.'
        },
        {
          question: 'In an A2A system, how do agents communicate with each other?',
          options: [
            'Through voice calls',
            'Via email messages',
            'Using structured JSON messages',
            'By sharing files'
          ],
          correct: 2,
          explanation: 'Agents communicate via structured JSON messages that follow a standardized protocol, ensuring reliable and predictable exchanges.'
        },
        {
          question: 'In the HR Recruitment example, which agent handles interview scheduling?',
          options: [
            'Screening Agent',
            'Communication Agent',
            'Assessment Agent',
            'Onboarding Agent'
          ],
          correct: 1,
          explanation: 'The Communication Agent is responsible for scheduling interviews automatically, coordinating between candidates and interviewers.'
        },
        {
          question: 'What is a key benefit of A2A communication?',
          options: [
            'Agents work independently without coordination',
            'Manual intervention is required for every step',
            'Complex workflows are distributed across specialized agents',
            'Only one agent is needed for all tasks'
          ],
          correct: 2,
          explanation: 'A2A allows complex workflows to be broken down and distributed across specialized agents, each handling specific tasks efficiently.'
        }
      ]
    },
    capabilities: {
      questions: [
        {
          question: 'What is the first step in A2A communication?',
          options: [
            'Task execution',
            'Capability Discovery',
            'Data storage',
            'User authentication'
          ],
          correct: 1,
          explanation: 'Capability Discovery is the first step, allowing agents to understand what other agents can do before attempting collaboration.'
        },
        {
          question: 'What information does a capability include?',
          options: [
            'Only the capability name',
            'Name, description, parameters, and expected outputs',
            'Just the agent\'s IP address',
            'Only error messages'
          ],
          correct: 1,
          explanation: 'Capabilities provide comprehensive information including name, description, required parameters, and expected outputs to enable proper integration.'
        },
        {
          question: 'When does capability discovery happen?',
          options: [
            'After task execution',
            'Only when errors occur',
            'Automatically when agents first connect',
            'Never - it\'s hardcoded'
          ],
          correct: 2,
          explanation: 'Discovery happens automatically when agents first connect, ensuring the client always has up-to-date information about available capabilities.'
        },
        {
          question: 'What is a key advantage of dynamic capability discovery?',
          options: [
            'Capabilities cannot be changed',
            'Capabilities can be added without updating the client',
            'Agents must be restarted frequently',
            'Discovery is slow and inefficient'
          ],
          correct: 1,
          explanation: 'Dynamic discovery means new capabilities can be added to the backend without requiring client updates, providing flexibility and maintainability.'
        }
      ]
    },
    'task-execution': {
      questions: [
        {
          question: 'What is required to execute a task in A2A communication?',
          options: [
            'Only the task name',
            'Task name and structured execute message with parameters',
            'Just a database connection',
            'Only the agent\'s IP address'
          ],
          correct: 1,
          explanation: 'Task execution requires a structured execute message containing the task name, required parameters, sender information, and often a signature.'
        },
        {
          question: 'Why is state management important in A2A workflows?',
          options: [
            'It\'s not important',
            'It ensures seamless experience even if connection drops',
            'It makes the system slower',
            'It only matters for databases'
          ],
          correct: 1,
          explanation: 'State management tracks execution progress across multi-step workflows, ensuring reliability and recovery if connections are interrupted.'
        },
        {
          question: 'What happens during a multi-step A2A workflow?',
          options: [
            'Only one step is executed',
            'State is maintained across all steps',
            'Each step requires manual intervention',
            'Steps cannot share information'
          ],
          correct: 1,
          explanation: 'In multi-step workflows, state is maintained across all steps, allowing agents to track progress and share context throughout the process.'
        },
        {
          question: 'How do agents handle errors during task execution?',
          options: [
            'They ignore errors completely',
            'They crash and restart',
            'Error handling ensures robustness and recovery',
            'Errors are only logged, never handled'
          ],
          correct: 2,
          explanation: 'Agents implement comprehensive error handling to ensure robustness, including retry logic, graceful degradation, and informative error messages.'
        }
      ]
    },
    security: {
      questions: [
        {
          question: 'How are A2A messages secured?',
          options: [
            'They are not secured',
            'Using HMAC-SHA256 message signing',
            'With passwords only',
            'Through IP address filtering'
          ],
          correct: 1,
          explanation: 'Messages are signed using HMAC-SHA256, which provides authentication and ensures message integrity without transmitting secret keys.'
        },
        {
          question: 'What do both agents verify before processing messages?',
          options: [
            'Message length',
            'Message color',
            'Message signatures',
            'Message speed'
          ],
          correct: 2,
          explanation: 'Both agents verify message signatures to ensure authenticity and detect any tampering before processing the message content.'
        },
        {
          question: 'Are secret keys transmitted between agents?',
          options: [
            'Yes, with every message',
            'No, they are never transmitted',
            'Only during initial connection',
            'Only when errors occur'
          ],
          correct: 1,
          explanation: 'Secret keys are never transmitted. They are used locally to sign and verify messages, preventing interception attacks.'
        },
        {
          question: 'What security mechanism prevents API abuse?',
          options: [
            'Password complexity requirements',
            'Rate limiting',
            'Message length restrictions',
            'Time delays'
          ],
          correct: 1,
          explanation: 'Rate limiting prevents abuse by restricting the number of requests an agent can make within a time window, protecting against DoS attacks.'
        }
      ]
    },
    'ux-negotiation': {
      questions: [
        {
          question: 'What is UX Negotiation in A2A systems?',
          options: [
            'Arguing about design choices',
            'Dynamically adjusting the UI based on context and user needs',
            'Creating static layouts',
            'Removing all UI elements'
          ],
          correct: 1,
          explanation: 'UX Negotiation allows agents to dynamically adapt the interface based on factors like user level, device capabilities, and content type.'
        },
        {
          question: 'How can the backend influence the UI?',
          options: [
            'It cannot influence the UI at all',
            'By recommending UI changes based on content and context',
            'By forcing specific colors only',
            'By blocking user interactions'
          ],
          correct: 1,
          explanation: 'The backend can recommend UI adaptations based on content complexity, user proficiency, device type, and other contextual factors.'
        },
        {
          question: 'What UI adjustment might be recommended for a beginner user?',
          options: [
            'Remove all help features',
            'More visuals, step-by-step guidance, and tooltips',
            'Show only advanced features',
            'Disable all interactions'
          ],
          correct: 1,
          explanation: 'For beginners, the system recommends more visual aids, clear step-by-step instructions, and helpful tooltips to support learning.'
        },
        {
          question: 'Why does personalized UX improve learning outcomes?',
          options: [
            'It doesn\'t improve outcomes',
            'It adapts to individual needs and learning styles',
            'It makes everything more complex',
            'It removes all content'
          ],
          correct: 1,
          explanation: 'Personalized UX adapts to individual learning styles, pace, and needs, making content more accessible and effective for each user.'
        }
      ]
    }
  };

  const currentQuiz = quizzes[topic] || quizzes.introduction;
  const totalQuestions = currentQuiz.questions.length;
  const currentQ = currentQuiz.questions[currentQuestion];

  const handleSelectAnswer = (optionIndex) => {
    if (!showResults) {
      setSelectedAnswers({
        ...selectedAnswers,
        [currentQuestion]: optionIndex
      });
    }
  };

  const handleNext = () => {
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = () => {
    // Calculate score
    let correctCount = 0;
    currentQuiz.questions.forEach((q, idx) => {
      if (selectedAnswers[idx] === q.correct) {
        correctCount++;
      }
    });
    
    const finalScore = Math.round((correctCount / totalQuestions) * 100);
    setScore(finalScore);
    setShowResults(true);
    
    // Notify parent
    if (onComplete) {
      onComplete(finalScore);
    }
  };

  const isAnswered = selectedAnswers[currentQuestion] !== undefined;
  const allAnswered = Object.keys(selectedAnswers).length === totalQuestions;
  const isCorrect = selectedAnswers[currentQuestion] === currentQ.correct;

  if (showResults) {
    return (
      <div className="card p-8 animate-fadeIn">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full mb-4">
            <Award className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Quiz Complete!</h2>
          <p className="text-gray-600">Here's how you did on {topicTitle}</p>
        </div>

        {/* Score Display */}
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-8 mb-8">
          <div className="text-center">
            <div className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 mb-2">
              {score}%
            </div>
            <div className="text-gray-700 text-lg">
              {score >= 90 ? '🌟 Outstanding! You\'ve mastered this topic!' :
               score >= 70 ? '👍 Great job! You have a solid understanding.' :
               score >= 50 ? '📚 Good start! Review the material and try again.' :
               '💪 Keep learning! Practice makes perfect.'}
            </div>
          </div>
        </div>

        {/* Question Review */}
        <div className="space-y-4 mb-8">
          <h3 className="font-bold text-gray-900 text-lg">Review Your Answers</h3>
          {currentQuiz.questions.map((q, idx) => {
            const userAnswer = selectedAnswers[idx];
            const correct = userAnswer === q.correct;

            return (
              <div key={idx} className={`p-4 rounded-lg border-2 ${correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-start space-x-3 mb-2">
                  {correct ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 mb-1">
                      Question {idx + 1}: {q.question}
                    </div>
                    <div className="text-sm text-gray-700">
                      Your answer: <span className={correct ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                        {q.options[userAnswer]}
                      </span>
                    </div>
                    {!correct && (
                      <div className="text-sm text-gray-700 mt-1">
                        Correct answer: <span className="text-green-700 font-medium">
                          {q.options[q.correct]}
                        </span>
                      </div>
                    )}
                    <div className="mt-2 text-sm text-gray-600 italic">
                      💡 {q.explanation}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <button onClick={onBack} className="btn-secondary flex items-center space-x-2">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Topic</span>
          </button>
          <button onClick={() => window.location.reload()} className="btn-primary flex-1">
            Continue Learning
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-8 animate-fadeIn">
      {/* Quiz Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Quiz: {topicTitle}</h2>
          <p className="text-gray-600 mt-1">Test your understanding</p>
        </div>
        <button onClick={onBack} className="btn-secondary flex items-center space-x-2">
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Question {currentQuestion + 1} of {totalQuestions}</span>
          <span>{Object.keys(selectedAnswers).length} / {totalQuestions} answered</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-indigo-600 to-purple-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestion + 1) / totalQuestions) * 100}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="mb-8">
        <h3 className="text-xl font-bold text-gray-900 mb-6">{currentQ.question}</h3>
        
        {/* Options */}
        <div className="space-y-3">
          {currentQ.options.map((option, idx) => {
            const isSelected = selectedAnswers[currentQuestion] === idx;
            
            return (
              <button
                key={idx}
                onClick={() => handleSelectAnswer(idx)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-indigo-600 bg-indigo-50'
                    : 'border-gray-200 bg-white hover:border-indigo-300'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    isSelected
                      ? 'border-indigo-600 bg-indigo-600'
                      : 'border-gray-300'
                  }`}>
                    {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                  </div>
                  <span className="text-gray-900">{option}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Show explanation after answering */}
        {isAnswered && (
          <div className={`mt-6 p-4 rounded-lg ${
            isCorrect ? 'bg-green-50 border-2 border-green-200' : 'bg-yellow-50 border-2 border-yellow-200'
          } animate-fadeIn`}>
            <div className="flex items-start space-x-3">
              <Lightbulb className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isCorrect ? 'text-green-600' : 'text-yellow-600'}`} />
              <div>
                <div className={`font-medium mb-1 ${isCorrect ? 'text-green-900' : 'text-yellow-900'}`}>
                  {isCorrect ? '✓ Correct!' : 'Not quite right'}
                </div>
                <div className="text-sm text-gray-700">{currentQ.explanation}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentQuestion === 0}
          className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>

        <div className="flex space-x-2">
          {Array.from({ length: totalQuestions }).map((_, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full ${
                selectedAnswers[idx] !== undefined
                  ? 'bg-indigo-600'
                  : idx === currentQuestion
                  ? 'bg-indigo-300'
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>

        {currentQuestion < totalQuestions - 1 ? (
          <button
            onClick={handleNext}
            disabled={!isAnswered}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next Question
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={!allAnswered}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-8"
          >
            Submit Quiz
          </button>
        )}
      </div>
    </div>
  );
};

export default QuizComponent;

